import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ThermometerIcon, DropletsIcon, ZapIcon, PowerIcon, ActivityIcon, SettingsIcon, LightbulbIcon, ToggleLeftIcon, GaugeIcon, WindIcon, CloudIcon } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line } from 'recharts';
type SensorType = 'SEO' | 'SEL' | 'SES';
type TimeRange = 'realtime' | 'daily' | 'weekly' | 'monthly';
type Mode = 'normal' | 'occupancy' | 'co2' | 'condensation' | 'ai';
interface Sensor {
  id: string;
  type: SensorType;
  label: string;
  roomId: string;
  active: boolean;
}
interface SensorDetailSidebarProps {
  sensor: Sensor;
  onBack: () => void;
  onClose: () => void;
}
const MODES: {
  id: Mode;
  label: string;
}[] = [{
  id: 'normal',
  label: '일반'
}, {
  id: 'occupancy',
  label: '재실'
}, {
  id: 'co2',
  label: 'CO2'
}, {
  id: 'condensation',
  label: '결로'
}, {
  id: 'ai',
  label: 'AI선정'
}];
const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  realtime: '실시간',
  daily: '일간',
  weekly: '주간',
  monthly: '월간'
};
// Mock Data generators
const genData = (len: number, labels: string[], base: number, variance: number) => labels.map(name => ({
  name,
  value: +(base + Math.random() * variance).toFixed(1)
}));
const MOCK_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 20, 5),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 18, 8),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 20, 10),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 15, 15)
};
// SEL-specific mock data
const SEL_POWER_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 8, 4),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 5, 12),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 1.2, 3),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 0.8, 4)
};
const SEL_PRESSURE_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 1010, 8),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 1008, 12),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 1005, 15),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 1000, 20)
};
// SES-specific mock data
const SES_TVOC_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 120, 80),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 100, 150),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 80, 200),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 90, 180)
};
const SES_CO2_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 400, 200),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 380, 300),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 350, 350),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 360, 320)
};
const SES_DUST_DATA: Record<TimeRange, {
  name: string;
  value: number;
}[]> = {
  realtime: genData(12, Array.from({
    length: 12
  }, (_, i) => `${i * 5}m`), 15, 20),
  daily: genData(24, Array.from({
    length: 24
  }, (_, i) => `${i}h`), 12, 30),
  weekly: genData(7, ['월', '화', '수', '목', '금', '토', '일'], 10, 40),
  monthly: genData(30, Array.from({
    length: 30
  }, (_, i) => `${i + 1}`), 8, 45)
};
// Reusable time range tab bar
function TimeRangeTabs({
  value,
  onChange
}: {
  value: TimeRange;
  onChange: (t: TimeRange) => void;
}) {
  return <div className="flex bg-white/5 rounded-lg p-0.5" data-id="element-250">
      {(['realtime', 'daily', 'weekly', 'monthly'] as TimeRange[]).map(t => <button key={t} onClick={() => onChange(t)} className={`
            px-2 py-1 rounded-md text-[9px] uppercase font-bold transition-all
            ${value === t ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}
          `} data-id="element-251">
          {t.slice(0, 1).toUpperCase()}
        </button>)}
    </div>;
}
// Reusable chart section
function ChartSection({
  icon: Icon,
  iconColor,
  title,
  data,
  timeRange,
  onTimeRangeChange,
  chartType = 'area',
  color,
  gradientId,
  stats
}: {
  icon: any;
  iconColor: string;
  title: string;
  data: {
    name: string;
    value: number;
  }[];
  timeRange: TimeRange;
  onTimeRangeChange: (t: TimeRange) => void;
  chartType?: 'area' | 'bar' | 'line';
  color: string;
  gradientId: string;
  stats?: {
    label: string;
    value: string;
    color?: string;
  }[];
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="glass-panel rounded-xl p-4" data-id="element-252">
      <div className="flex items-center justify-between mb-3" data-id="element-253">
        <div className="flex items-center gap-2" data-id="element-254">
          <Icon className={`w-4 h-4 ${iconColor}`} data-id="element-255" />
          <span className="text-xs font-bold text-slate-300" data-id="element-256">{title}</span>
        </div>
        <TimeRangeTabs value={timeRange} onChange={onTimeRangeChange} data-id="element-257" />
      </div>

      <div className="h-[100px] w-full" data-id="element-258">
        <ResponsiveContainer width="100%" height="100%" data-id="element-259">
          {chartType === 'bar' ? <BarChart data={data} data-id="element-260">
              <XAxis dataKey="name" hide data-id="element-261" />
              <Tooltip cursor={{
            fill: 'rgba(255,255,255,0.05)'
          }} contentStyle={{
            backgroundColor: '#0a0f1a',
            borderColor: '#334155',
            fontSize: '10px'
          }} itemStyle={{
            color
          }} data-id="element-262" />
              <Bar dataKey="value" fill={color} radius={[2, 2, 0, 0]} opacity={0.8} data-id="element-263" />
            </BarChart> : chartType === 'line' ? <LineChart data={data} data-id="element-264">
              <XAxis dataKey="name" hide data-id="element-265" />
              <Tooltip contentStyle={{
            backgroundColor: '#0a0f1a',
            borderColor: '#334155',
            fontSize: '10px'
          }} itemStyle={{
            color
          }} data-id="element-266" />
              <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} data-id="element-267" />
            </LineChart> : <AreaChart data={data} data-id="element-268">
              <defs data-id="element-269">
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1" data-id="element-270">
                  <stop offset="5%" stopColor={color} stopOpacity={0.3} data-id="element-271" />
                  <stop offset="95%" stopColor={color} stopOpacity={0} data-id="element-272" />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" hide data-id="element-273" />
              <YAxis hide data-id="element-274" />
              <Tooltip contentStyle={{
            backgroundColor: '#0a0f1a',
            borderColor: '#334155',
            fontSize: '10px'
          }} itemStyle={{
            color
          }} data-id="element-275" />
              <Area type="monotone" dataKey="value" stroke={color} fillOpacity={1} fill={`url(#${gradientId})`} data-id="element-276" />
            </AreaChart>}
        </ResponsiveContainer>
      </div>

      {stats && <div className="flex justify-between mt-2 text-xs" data-id="element-277">
          {stats.map((s, i) => <div key={i} className="text-center" data-id="element-278">
              <div className="text-slate-500 text-[10px] uppercase" data-id="element-279">
                {s.label}
              </div>
              <div className={`font-mono ${s.color || 'text-white'}`} data-id="element-280">
                {s.value}
              </div>
            </div>)}
        </div>}
    </motion.div>;
}
// ─── SEL Detail View ───
function SELDetail({
  sensor,
  onBack,
  onClose
}: SensorDetailSidebarProps) {
  const [powerRange, setPowerRange] = useState<TimeRange>('realtime');
  const [tempRange, setTempRange] = useState<TimeRange>('realtime');
  const [humidityRange, setHumidityRange] = useState<TimeRange>('realtime');
  const [pressureRange, setPressureRange] = useState<TimeRange>('realtime');
  return <div className="w-[320px] flex-shrink-0 flex flex-col gap-3 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-y-auto" data-id="element-281">
      <SensorHeader sensor={sensor} subtitle="Smart Edge Light" onBack={onBack} onClose={onClose} data-id="element-282" />

      <ChartSection icon={ZapIcon} iconColor="text-amber-400" title="소비전력" data={SEL_POWER_DATA[powerRange]} timeRange={powerRange} onTimeRangeChange={setPowerRange} chartType="bar" color="#fbbf24" gradientId="selPower" stats={[{
      label: '현재',
      value: '12W',
      color: 'text-amber-400'
    }, {
      label: '일평균',
      value: '8.4W'
    }]} data-id="element-283" />

      <ChartSection icon={ThermometerIcon} iconColor="text-rose-400" title="실내온도" data={MOCK_DATA[tempRange]} timeRange={tempRange} onTimeRangeChange={setTempRange} chartType="area" color="#fb7185" gradientId="selTemp" stats={[{
      label: '현재',
      value: '24.5°C',
      color: 'text-rose-400'
    }, {
      label: '평균',
      value: '23.8°C'
    }]} data-id="element-284" />

      <ChartSection icon={DropletsIcon} iconColor="text-cyan-400" title="습도" data={MOCK_DATA[humidityRange]} timeRange={humidityRange} onTimeRangeChange={setHumidityRange} chartType="area" color="#22d3ee" gradientId="selHumidity" stats={[{
      label: '현재',
      value: '48%',
      color: 'text-cyan-400'
    }, {
      label: '평균',
      value: '46.2%'
    }]} data-id="element-285" />

      <ChartSection icon={GaugeIcon} iconColor="text-indigo-400" title="기압" data={SEL_PRESSURE_DATA[pressureRange]} timeRange={pressureRange} onTimeRangeChange={setPressureRange} chartType="line" color="#818cf8" gradientId="selPressure" stats={[{
      label: '현재',
      value: '1013 hPa',
      color: 'text-indigo-400'
    }, {
      label: '추세',
      value: '↑ 안정'
    }]} data-id="element-286" />
    </div>;
}
// ─── SES Detail View ───
function SESDetail({
  sensor,
  onBack,
  onClose
}: SensorDetailSidebarProps) {
  const [tvocRange, setTvocRange] = useState<TimeRange>('realtime');
  const [co2Range, setCo2Range] = useState<TimeRange>('realtime');
  const [dustRange, setDustRange] = useState<TimeRange>('realtime');
  const tvocValue = 185;
  const co2Value = 520;
  const dustValue = 22;
  const tvocStatus = tvocValue < 200 ? '좋음' : tvocValue < 400 ? '보통' : '나쁨';
  const tvocColor = tvocValue < 200 ? 'text-emerald-400' : tvocValue < 400 ? 'text-amber-400' : 'text-red-400';
  const co2Status = co2Value < 600 ? '좋음' : co2Value < 1000 ? '보통' : '나쁨';
  const co2Color = co2Value < 600 ? 'text-emerald-400' : co2Value < 1000 ? 'text-amber-400' : 'text-red-400';
  const dustStatus = dustValue < 30 ? '좋음' : dustValue < 80 ? '보통' : '나쁨';
  const dustColor = dustValue < 30 ? 'text-emerald-400' : dustValue < 80 ? 'text-amber-400' : 'text-red-400';
  return <div className="w-[320px] flex-shrink-0 flex flex-col gap-3 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-y-auto" data-id="element-287">
      <SensorHeader sensor={sensor} subtitle="Smart Switch" onBack={onBack} onClose={onClose} data-id="element-288" />

      {/* Quick Status Overview */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="grid grid-cols-3 gap-2" data-id="element-289">
        <div className="glass-panel rounded-xl p-3 text-center" data-id="element-290">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mb-1" data-id="element-291">
            TVOC
          </div>
          <div className={`text-lg font-mono font-bold ${tvocColor}`} data-id="element-292">
            {tvocValue}
          </div>
          <div className={`text-[9px] font-bold ${tvocColor}`} data-id="element-293">
            {tvocStatus}
          </div>
        </div>
        <div className="glass-panel rounded-xl p-3 text-center" data-id="element-294">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mb-1" data-id="element-295">
            CO₂
          </div>
          <div className={`text-lg font-mono font-bold ${co2Color}`} data-id="element-296">
            {co2Value}
          </div>
          <div className={`text-[9px] font-bold ${co2Color}`} data-id="element-297">{co2Status}</div>
        </div>
        <div className="glass-panel rounded-xl p-3 text-center" data-id="element-298">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mb-1" data-id="element-299">
            미세먼지
          </div>
          <div className={`text-lg font-mono font-bold ${dustColor}`} data-id="element-300">
            {dustValue}
          </div>
          <div className={`text-[9px] font-bold ${dustColor}`} data-id="element-301">
            {dustStatus}
          </div>
        </div>
      </motion.div>

      <ChartSection icon={WindIcon} iconColor="text-emerald-400" title="TVOC" data={SES_TVOC_DATA[tvocRange]} timeRange={tvocRange} onTimeRangeChange={setTvocRange} chartType="area" color="#34d399" gradientId="sesTvoc" stats={[{
      label: '현재',
      value: `${tvocValue} ppb`,
      color: tvocColor
    }, {
      label: '평균',
      value: '165 ppb'
    }]} data-id="element-302" />

      <ChartSection icon={CloudIcon} iconColor="text-orange-400" title="CO₂" data={SES_CO2_DATA[co2Range]} timeRange={co2Range} onTimeRangeChange={setCo2Range} chartType="area" color="#fb923c" gradientId="sesCo2" stats={[{
      label: '현재',
      value: `${co2Value} ppm`,
      color: co2Color
    }, {
      label: '평균',
      value: '480 ppm'
    }]} data-id="element-303" />

      <ChartSection icon={ActivityIcon} iconColor="text-pink-400" title="미세먼지 (PM2.5)" data={SES_DUST_DATA[dustRange]} timeRange={dustRange} onTimeRangeChange={setDustRange} chartType="bar" color="#f472b6" gradientId="sesDust" stats={[{
      label: 'PM2.5',
      value: `${dustValue} µg/m³`,
      color: dustColor
    }, {
      label: 'PM10',
      value: '38 µg/m³'
    }]} data-id="element-304" />
    </div>;
}
// ─── Shared Header ───
function SensorHeader({
  sensor,
  subtitle,
  onBack,
  onClose
}: {
  sensor: Sensor;
  subtitle: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const getIcon = () => {
    switch (sensor.type) {
      case 'SEO':
        return ZapIcon;
      case 'SEL':
        return LightbulbIcon;
      case 'SES':
        return ToggleLeftIcon;
      default:
        return ActivityIcon;
    }
  };
  const Icon = getIcon();
  return <div className="flex items-center gap-3 mb-1 flex-shrink-0" data-id="element-305">
      <button onClick={onBack} className="p-2 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white" data-id="element-306">
        <ArrowLeftIcon className="w-5 h-5" data-id="element-307" />
      </button>
      <div className="flex-1" data-id="element-308">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500" data-id="element-309">
          {subtitle}
        </div>
        <div className="text-lg font-bold text-white flex items-center gap-2" data-id="element-310">
          {sensor.label}
          <span className={`w-2 h-2 rounded-full ${sensor.active ? 'bg-emerald-400' : 'bg-slate-600'}`} data-id="element-311" />
        </div>
      </div>
      <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white" data-id="element-312">
        <SettingsIcon className="w-4 h-4" data-id="element-313" />
      </button>
    </div>;
}
// ─── Main Export ───
export function SensorDetailSidebar({
  sensor,
  onBack,
  onClose
}: SensorDetailSidebarProps) {
  const [envTimeRange, setEnvTimeRange] = useState<TimeRange>('realtime');
  const [powerTimeRange, setPowerTimeRange] = useState<TimeRange>('realtime');
  const [mode, setMode] = useState<Mode>('normal');
  const [isOn, setIsOn] = useState(true);
  // Route to specific sensor type views
  if (sensor.type === 'SEL') {
    return <SELDetail sensor={sensor} onBack={onBack} onClose={onClose} data-id="element-314" />;
  }
  if (sensor.type === 'SES') {
    return <SESDetail sensor={sensor} onBack={onBack} onClose={onClose} data-id="element-315" />;
  }
  // ─── SEO (Outlet) View ───
  return <div className="w-[320px] flex-shrink-0 flex flex-col gap-3 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-y-auto" data-id="element-316">
      <SensorHeader sensor={sensor} subtitle="Smart Edge Outlet" onBack={onBack} onClose={onClose} data-id="element-317" />

      {/* Mode Selection */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="glass-panel rounded-xl p-3" data-id="element-318">
        <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-3 px-1" data-id="element-319">
          Operation Mode
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3" data-id="element-320">
          {MODES.map(m => <button key={m.id} onClick={() => setMode(m.id)} className={`
                px-2 py-2 rounded-lg text-xs font-medium transition-all
                ${mode === m.id ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-slate-400 border border-transparent hover:bg-white/10'}
              `} data-id="element-321">
              {m.label}
            </button>)}
        </div>

        {mode === 'normal' && <div className="flex items-center justify-between bg-white/5 rounded-lg p-3 border border-white/5" data-id="element-322">
            <span className="text-sm text-slate-300 font-medium" data-id="element-323">
              전원
            </span>
            <button onClick={() => setIsOn(!isOn)} className={`
                relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none
                ${isOn ? 'bg-emerald-500' : 'bg-slate-700'}
              `} data-id="element-324">
              <div className={`
                  absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-300
                  ${isOn ? 'translate-x-6' : 'translate-x-0'}
                `} data-id="element-325" />
            </button>
          </div>}
      </motion.div>

      <ChartSection icon={ThermometerIcon} iconColor="text-amber-400" title="온습도" data={MOCK_DATA[envTimeRange]} timeRange={envTimeRange} onTimeRangeChange={setEnvTimeRange} chartType="area" color="#f59e0b" gradientId="seoEnv" stats={[{
      label: '온도',
      value: '24.5°C'
    }, {
      label: '습도',
      value: '48%',
      color: 'text-cyan-400'
    }]} data-id="element-326" />

      <ChartSection icon={ZapIcon} iconColor="text-violet-400" title="전력사용량" data={MOCK_DATA[powerTimeRange]} timeRange={powerTimeRange} onTimeRangeChange={setPowerTimeRange} chartType="bar" color="#a78bfa" gradientId="seoPower" stats={[{
      label: '현재 전력사용량',
      value: '145 W',
      color: 'text-violet-400'
    }]} data-id="element-327" />
    </div>;
}